/* eslint-disable indent */
import React, { Dispatch, FC, ReactElement, SetStateAction, useRef } from 'react';
import { View, Text, TouchableWithoutFeedback, StyleSheet, StyleProp, ViewStyle } from 'react-native';

import { addImageModal, IMAGE_HANDLING_HEADER_HEIGHT } from '@components/imageHandling/common';
import { Element, scale as initialScale, notchTop } from '@components/frame';
import { isIOS, isWeb, isPhone, isPhoneNative, isTabletOrWeb } from '@helpers';
import { font as initialFont } from '@styles/fonts';

import AttachModal from './components/AttachModal';
import RemoveAttachmentModal from './components/RemoveAttachmentModal';
import HeaderIcons, { HeaderIconsHandle } from './components/HeaderIcons';
import { HeaderIconKey } from './components/HeaderIcons/types';
import DocTypeButton from './components/DocTypeButton';
import InfoPopupWeb from './components/InfoPopup/InfoPopupWeb';
import InfoPopupTabletMobile from './components/InfoPopup/InfoPopupTabletMobile';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import {
  CropImageDocumentAction,
  ImageContextForAdding,
  ImageDocumentStoreItem,
  ImageHandlingDisplayMode,
  RemoveImageDocumentAction,
  RetrievedDocumentReferenceLink,
  RotateImageDocumentAction,
} from '../../types/types';
import { useManageImageHandlingHeader } from './hooks/useManageImageHandlingHeader';
import RotateModal from './components/RotateModal';
import ImageHandlingHeaderMobile from './HeaderMobile';
import { IMAGE_HANDLING_PLATFORM_TOP_FALLBACK } from '@v2/components/imageHandling/constants';
import { GetActionsConfig } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationDocuments/hooks/useManageOPInformationDocuments';
import CropperWeb from '@v2/components/imageHandling/components/Cropper/components/CropperWeb';
import CropperTablet from '@v2/components/imageHandling/components/Cropper/components/CropperTablet';
import AttachButton from '@components/common/attachButton';
import ModalWrapper from '@components/modal/ModalWrapper';
import { ContainerStyle } from '@interfaces/base';
import { WithColor } from '@styles/colors';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

export interface ImageHandlingHeaderProps extends ContainerStyle, WithColor {
  //image actions
  rotateImage: RotateImageDocumentAction;
  deleteImage: RemoveImageDocumentAction;
  cropImage: CropImageDocumentAction;

  // other actions
  onDismiss?: () => void;
  onClose?: () => void;
  onSuccessImageUpload?: (id: string) => void;
  onExpandIconPress?: () => void;
  onCollapseIconPress?: () => void;
  onMobileBackButtonPress?: () => void;
  triggerDraw?: () => void;
  isDrawing?: boolean;

  //common
  scale?: ScaleFunction;
  font?: typeof initialFont;

  // boolean
  shouldHideDownloadButton?: boolean;
  shouldHideAttachButton?: boolean;
  shouldShowAddButtonIfSingleImage?: boolean;
  shouldCenterDocTypeModal?: boolean;
  shouldHideThumbnailButton?: boolean;
  shouldShowAddImageModal?: boolean;
  shouldDisplayModeIcon?: boolean;
  shouldHideIconsWithoutImg?: boolean;
  showActions?: boolean;
  withoutDocTypeButton?: boolean;
  withoutAttachButton?: boolean;
  withoutFavoriteButton?: boolean;
  withNotchTop?: boolean;

  // state
  imageContextForAdding: ImageContextForAdding;
  images: ImageDocumentStoreItem[];
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  navigateBackFallBackScreen?: string;
  setMode?: Dispatch<SetStateAction<ImageHandlingDisplayMode>>;
  getInfoPopupActionsConfig?: GetActionsConfig;
  enabledIcons?: HeaderIconKey[];

  //styles
  bg?: string;
  borderColor?: string;
  iconsContainerStyle?: StyleProp<ViewStyle>;

  // components
  TitleComponent?: ReactElement;
  BackButtonComponent?: ReactElement | null;
}

const ImageHandlingHeader: FC<ImageHandlingHeaderProps> = (props) => {
  const headerIconsRef = useRef<HeaderIconsHandle>({});

  const {
    C,
    images,
    selectedIndex,
    shouldShowAddImageModal = false,
    rotateImage,
    deleteImage,
    cropImage,
    setSelectedIndex,
    bg,
    borderColor,
    withoutDocTypeButton,
    TitleComponent,
    BackButtonComponent,
    shouldHideIconsWithoutImg,
    containerStyle,
    scale = initialScale,
    font = initialFont,
    navigateBackFallBackScreen,
    onDismiss,
    onClose,
    shouldHideDownloadButton,
    shouldHideAttachButton,
    shouldShowAddButtonIfSingleImage,
    shouldHideThumbnailButton,
    imageContextForAdding,
    shouldDisplayModeIcon,
    iconsContainerStyle,
    onSuccessImageUpload,
    onMobileBackButtonPress,
    setMode,
    getInfoPopupActionsConfig,
    withoutAttachButton,
    withoutFavoriteButton,
    onExpandIconPress,
    onCollapseIconPress,
    shouldCenterDocTypeModal = true,
    withNotchTop = true,
    showActions = true,
    enabledIcons,
    triggerDraw,
    isDrawing,
  } = props;

  const img = images[selectedIndex] ?? {};

  const {
    download,
    handleConfirmRemoveAttachment,
    handleRemoveAttachment,
    toggleAttachModal,
    isSingleImage,
    setShowAttachModal,
    setShowRemoveAttachmentModal,
    showAttachModal,
    showInfoPopUp,
    showRemoveAttachmentModal,
    showRotatePopUp,
    triggerInfoPopUp,
    triggerRotatePopUp,
    handleToggleFavoriteLink,
    triggerCrop,
    infoPopupActions,
    showCrop,
    onSaveCrop,
    imageContext,
    shouldDisplayFavoriteButton,
  } = useManageImageHandlingHeader({
    cropImage,
    images,
    img,
    selectedIndex,
    getInfoPopupActionsConfig,
    withoutFavoriteButton,
  });

  const topOffset = isIOS && withNotchTop && !showCrop && notchTop === 0 ? IMAGE_HANDLING_PLATFORM_TOP_FALLBACK : 0;

  const infoPopupProps = {
    C,
    deleteImage,
    font,
    scale,
    images,
    onClose: triggerInfoPopUp,
    selectedIndex,
    setSelectedIndex,
    visible: showInfoPopUp,
    list: infoPopupActions,
    ...(isTabletOrWeb ? { iconPosition: headerIconsRef.current?.moreIcon } : {}),
  };

  if (isPhoneNative) {
    return (
      <ImageHandlingHeaderMobile
        C={C}
        imageContextForAdding={imageContextForAdding}
        images={images}
        isSingleImage={isSingleImage}
        selectedIndex={selectedIndex}
        setMode={setMode}
        showAddImageModal={shouldShowAddImageModal}
        onBackButtonPress={onMobileBackButtonPress}
        font={font}
        scale={scale}
      />
    );
  }

  return [
    !isWeb && !isPhone && isIOS && (
      <View
        key="StatusBarBackground"
        style={{
          backgroundColor: bg ?? C('Background/Light/menuApp'),
          position: 'absolute',
          width: '100%',
          height: scale(topOffset),
          top: 0,
          left: 0,
          zIndex: 100000,
        }}
      />
    ),
    showActions && (showInfoPopUp || showAttachModal) && (
      <TouchableWithoutFeedback
        key="modalOverlay"
        onPress={() => {
          if (showInfoPopUp) triggerInfoPopUp();
          if (showAttachModal) setShowAttachModal(false);
        }}
      >
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 5000,
          }}
        />
      </TouchableWithoutFeedback>
    ),
    <Element
      key="HeaderBackground"
      {...{
        height: IMAGE_HANDLING_HEADER_HEIGHT + topOffset,
        zIndex: showActions ? 1000 : 0,
        display: showActions ? 'flex' : 'none',
        opacity: showActions ? 1 : 0,
        backgroundColor: bg ?? C('Background/Light/menuApp'),
        scale,
      }}
    />,
    <Element
      key="Header"
      {...{
        config: {
          position: {
            top: topOffset,
            left: 0,
            right: 0,
          },
          height: IMAGE_HANDLING_HEADER_HEIGHT,
          zIndex: showActions ? 10000 : 0,
          display: showActions ? 'flex' : 'none',
          opacity: showActions ? 1 : 0,
          backgroundColor: bg ?? C('Background/Light/menuApp'),
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: borderColor ?? C('Hairline/Light/Header'),
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingHorizontal: scale(18),
          ...containerStyle,
        },
        scale,
      }}
    >
      {/* Header */}

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(22) }}>
        {!withoutAttachButton && <AttachButton {...{ C, shouldUseRelativeLeftPosition: true, images, onNavigate: onDismiss, selectedIndex }} />}
        {!withoutDocTypeButton && <DocTypeButton {...{ C, img, images, shouldCenterModal: shouldCenterDocTypeModal }} />}
      </View>

      {(BackButtonComponent || TitleComponent) && (
        <View
          style={[
            { flexDirection: 'row', gap: scale(6), alignItems: 'center', flex: 1 },
            BackButtonComponent && { margin: scale(-12), marginRight: scale(5) },
          ]}
        >
          {BackButtonComponent && BackButtonComponent}
          {TitleComponent && TitleComponent}
        </View>
      )}

      {!TitleComponent && (
        <View
          style={{
            flex: 1,
            height: '100%',
            justifyContent: 'center',
            alignSelf: 'flex-start',
            marginLeft: scale(16),
          }}
        >
          <Text testID={TEST_IDS.documentsHeader.title} style={[font['Subhead 1'], { color: C('Label/Light/Primary') }]} numberOfLines={1} ellipsizeMode={'middle'}>
            {img.fileName}
          </Text>
        </View>
      )}

      {/* Icons container */}
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'flex-end',
          alignItems: 'center',
          zIndex: 10000,
          height: scale(isWeb ? 32 : 22),
          // flex: 1,
        }}
      >
        <HeaderIcons
          key="HeaderIcons"
          ref={headerIconsRef}
          {...{
            triggerRotatePopUp,
            rotateImage,
            img,
            toggleAttachModal,
            isSingleImage,
            addImageModal,
            shouldShowAddImageModal,
            triggerInfoPopUp,
            showInfoPopUp,
            showRotatePopUp,
            triggerCrop,
            showAttachModal,
            download,
            setMode,
            shouldHideIconsWithoutImg,
            scale,
            font,
            C,
            navigateBackFallBackScreen,
            onClose,
            shouldHideDownloadButton,
            shouldHideAttachButton,
            shouldShowAddButtonIfSingleImage,
            imageContextForAdding,
            shouldDisplayModeIcon,
            iconsContainerStyle,
            onSuccessImageUpload,
            shouldHideThumbnailButton,
            onExpandIconPress,
            onCollapseIconPress,
            enabledIcons,
            triggerDraw,
            isDrawing,
          }}
        />
      </View>
    </Element>,

    <AttachModal
      key="attachModal"
      visible={showAttachModal}
      onClose={toggleAttachModal}
      onNavigate={onDismiss}
      links={(img?.links || []) as RetrievedDocumentReferenceLink[]}
      onToggleFavorite={handleToggleFavoriteLink}
      onRemoveAttachment={handleRemoveAttachment}
      imageId={img?.id}
      fileName={img?.fileName}
      img={img}
      scale={scale}
      font={font}
    />,

    <RemoveAttachmentModal
      key="removeAttachmentModal"
      visible={showRemoveAttachmentModal}
      onClose={() => setShowRemoveAttachmentModal(false)}
      onConfirm={handleConfirmRemoveAttachment}
    />,

    <RotateModal
      key="rotateModal"
      C={C}
      rotateImage={rotateImage}
      showRotatePopUp={showRotatePopUp}
      triggerRotatePopUp={triggerRotatePopUp}
      images={images}
      selectedIndex={selectedIndex}
      {...(isTabletOrWeb ? { iconPosition: headerIconsRef.current?.rotateIcon } : {})}
    />,

    isWeb && <InfoPopupWeb {...infoPopupProps} />,
    !isWeb && <InfoPopupTabletMobile {...infoPopupProps} />,
    showCrop && isWeb && (
      <CropperWeb
        key="headerCropImageWeb"
        {...{
          onBack: triggerCrop,
          onSave: onSaveCrop,
          image: images[selectedIndex] || {},
          shouldSaveOnDoneClickIfNotDirty: false,
          withoutAnimation: true,
        }}
      />
    ),
    showCrop && !isWeb && !isPhone && (
      <ModalWrapper isVisible={true} C={C} onBackdropPress={() => {}} withoutAnimation>
        <CropperTablet
          key="headerCropImageTablet"
          {...{
            triggerShow: triggerCrop,
            onSave: onSaveCrop,
            image: images[selectedIndex] || {},
          }}
        />
      </ModalWrapper>
    ),
  ];
};

export default ImageHandlingHeader;
